import React, {
  Children, ReactNode, Suspense, cloneElement, useEffect, useState,
} from 'react';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { showKioskSetupSelector } from '../../redux/selectors/sharedSelectors';
import { ContextProviderComponent } from '../sections/CompareToolBar/CompareToolBarContext';
import Header from '../sections/Header/Header';
import GlobalStyle from '../../styles/GlobalStyle';
import useActiveStore from '../../hooks/useActiveStore';
import SetupHeader from '../Kiosk/SetupHeader';
import openChatWidget from '../../utils/openChatWidget';

const Footer = React.lazy(() => import('../sections/Footer/Footer'));

const LocationBlade = React.lazy(() => import('../StoreLocator/LocationBlade/LocationBlade'));

// eslint-disable-next-line max-len
const renderChildrenWithProps = ({ props, children }) => Children.map(children, (child) => cloneElement(child, { ...props }));

// Returns bool based on lang prefix being valid: /en/ or /fr/
const isValidLanguagePrefix = (uri) => {
  const prefix = uri.slice(0, 4);
  const lang = uri.slice(1, 3);
  const isValidUriPrefix = prefix[0] === '/' && (!prefix[3] || prefix[3] === '/');
  const isValidLang = lang === 'en' || lang === 'fr';
  return isValidLang && isValidUriPrefix;
};

// If there is a saved user prov in local storage, use that on state initialization
// Else use ON as default
let initialProvState;
if (typeof window !== 'undefined' && window.localStorage.savedProvince) {
  initialProvState = window.localStorage.getItem('savedProvince');
} else {
  initialProvState = 'ON';
}

interface Props {
  location: Record<string, any>;
  locale: string;
  backgroundColor?: string;
  children?: ReactNode;
  footerWrapper?: boolean;
}

const Layout = ({
  children,
  location,
  locale = 'en',
  backgroundColor,
  footerWrapper = true,
}: Props) => {
  const [savedLanguage, setSavedLanguage] = useState(locale ?? 'en');
  const [savedProvince, setSavedProvince] = useState(initialProvState);
  const [ipLocation, setIpLocation] = useState(null);
  const showKioskSetup = useSelector(showKioskSetupSelector);
  const { activeStore } = useActiveStore();

  useEffect(() => {
    const urlLanguage = isValidLanguagePrefix(location.pathname)
      ? location.pathname.slice(1, 3)
      : undefined;
    const siteLanguage = location.host.indexOf('laboutiquemobile') === -1 ? 'en' : 'fr';

    const language = window.localStorage.getItem('savedLanguage');
    const browserLocale = window.navigator.userLanguage || window.navigator.language || undefined;

    const browserLanguage = (browserLocale?.slice(0, 2)) || undefined;

    setSavedLanguage(
      urlLanguage || language || siteLanguage || browserLanguage,
    );

    const urlParams = new URLSearchParams(window.location.search);
    const chatParam = urlParams.get('chat');
    if (chatParam !== null) {
      openChatWidget(null, chatParam);
    }
  }, [location.host, location.pathname]);

  // Navigate away from root pages to /en or /fr versions
  useEffect(() => {
    if (savedLanguage) {
      let newPath = '';
      const splitPath = location.pathname.slice(1).split('/');
      if (splitPath[0] !== 'en' && splitPath[0] !== 'fr') {
        newPath = `/${savedLanguage}/${splitPath.join('/')}`;
        navigate(newPath, { replace: true });
      }
    }
  }, [location.pathname, savedLanguage]);

  // Get IP address of (user?) on mount
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return;

    axios
      .get('/en/')
      .then((response) => {
        // * Split the response into Country, City, and Province. Looks like -> ["Country=CA", "City=BURLINGTON", "Province=ON"]
        const data = response.headers['x-geo-info'].split(',');
        const province = data[2].trim().split('=')[1] || 'ON';
        setIpLocation(province);
        return null;
      })
      .catch((err) => {
        console.warn('Fetch Error :-S', err);
      });
  }, []);

  // Click handler for lang pref in header
  const selectLocaleToSave = (event) => {
    if (typeof event.target.value === 'string') {
      const lang = event.target.value.slice(0, 2);
      setSavedLanguage(lang);
      // save the language for the users next wesbite visit.
      if (window) {
        window.localStorage.setItem('savedLanguage', lang);
      }

      // update the cookie banner language
      window.OneTrust?.changeLanguage?.(lang);

      // this will update the url to reflect the user's selected language
      const pathWithoutPrefix = window.location.pathname.slice(3);
      const searchParams = window.location.search;
      const isSafeURI = pathWithoutPrefix[0] === '/';
      const newPath = `/${lang}${
        isSafeURI ? pathWithoutPrefix : ''
      }${searchParams}`;
      navigate(newPath);
    }
  };

  // If active store is selected then set province to active store province
  // If savedProvince is already in local storage from previous session, get it and use it
  // Else, if there is no savedProvince in local storage, determine one via IP (?)
  // Else default to 'ON'
  useEffect(() => {
    if (typeof window !== 'undefined' && ipLocation !== null) {
      let province;
      if (activeStore) {
        province = activeStore.commonInfo.address.province.toUpperCase();
      } else {
        province = window.localStorage.getItem('savedProvince');
      }
      setSavedProvince(province || ipLocation || 'ON');
    }
  }, [ipLocation, activeStore]);

  // Set prov to local storage on initial mount (users first visit), as we need it there to compare prov changes in the MSA/filter system to know if we need to reload filters (carreirs etc)
  useEffect(() => {
    if (window) {
      window.localStorage.setItem('savedProvince', savedProvince);
    }
  }, [savedProvince]);

  // Handler for provSelector to save the chosen province to local Storage, when user changes it
  // This is used for future site visits
  const selectProvinceToSave = (event) => {
    if (typeof event.target.value === 'string') {
      const province = event.target.value.slice(0, 2).toUpperCase();
      setSavedProvince(province);
      if (window) {
        window.localStorage.setItem('savedProvince', province);
      }
    }
  };

  // Function to scroll and then set focus to main content with "Skip to Content" is clicked
  const skipToContentFocus = () => {
    if (typeof window !== 'undefined') {
      document.getElementById('main-content').scrollIntoView({
        behavior: 'smooth',
      });
      // Set short timeout to let scrolling to section finish
      setTimeout(() => {
        document.getElementById('main-content').focus();
      }, 300);
    }
  };

  const data = useStaticQuery(
    graphql`
      query LayoutQuery {
        siteHeaderEN: contentfulHeader(
          contentful_id: {eq: "32WWiqIbDJlMo8yi7SJ000"}
          node_locale: {eq: "en-CA"}
        ) {
          headerBlades {
            __typename
            contentful_id
            bladeSections {
              ... on ContentfulKeyValuePair {
                __typename
                contentful_id
                key
                value
              }
              ... on ContentfulForm {
                __typename
                contentful_id
                formFields {
                  __typename
                  contentful_id
                  fieldName
                  displayLabel
                }
              }
              ... on ContentfulCta {
                __typename
                contentful_id
                ctaType
                ctaTarget
                ctaColor
                ctaUrl
                ctaLabel {
                  ctaLabel
                }
                ctaAriaLabel
              }
            }
          }
          announcementBanner {
            contentful_id
            bannerColor
            bannerDisplayPages
            bannerDisplayProvinces
            isDismissible
            bannerContent {
              raw
              references {
                ... on ContentfulCta {
                  __typename
                  contentful_id
                  ctaType
                  ctaTarget
                  ctaUrl
                  ctaLabel {
                    ctaLabel
                  }
                }
              }
            }
          }
          logoDam {
            altText
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          headerLinks {
            ctaTarget
            ctaLabel {
              ctaLabel
            }
            ctaUrl
            ctaColor
            contentful_id
          }
          headerButtons {
            ... on ContentfulCta {
              ctaTarget
              ctaColor
              ctaLabel {
                ctaLabel
              }
              ctaUrl
              contentful_id
            }
          }
        }
        siteHeaderFR: contentfulHeader(
          contentful_id: {eq: "32WWiqIbDJlMo8yi7SJ000"}
          node_locale: {eq: "fr-CA"}
        ) {
          headerBlades {
            __typename
            contentful_id
            bladeSections {
              ... on ContentfulKeyValuePair {
                __typename
                contentful_id
                key
                value
              }
              ... on ContentfulForm {
                __typename
                contentful_id
                formFields {
                  __typename
                  contentful_id
                  fieldName
                  displayLabel
                }
              }
              ... on ContentfulCta {
                __typename
                contentful_id
                ctaType
                ctaTarget
                ctaColor
                ctaUrl
                ctaLabel {
                  ctaLabel
                }
                ctaAriaLabel
              }
            }
          }
          announcementBanner {
            contentful_id
            bannerColor
            bannerDisplayPages
            bannerDisplayProvinces
            isDismissible
            bannerContent {
              raw
              references {
                ... on ContentfulCta {
                  __typename
                  contentful_id
                  ctaType
                  ctaTarget
                  ctaUrl
                  ctaLabel {
                    ctaLabel
                  }
                }
              }
            }
          }
          logoDam {
            altText
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          headerLinks {
            ctaTarget
            ctaLabel {
              ctaLabel
            }
            ctaUrl
            ctaColor
            contentful_id
          }
          headerButtons {
            ... on ContentfulCta {
              ctaTarget
              ctaColor
              ctaLabel {
                ctaLabel
              }
              ctaUrl
              contentful_id
            }
          }
        }
        siteFooterEN: contentfulFooter(
          contentful_id: {eq: "zsNSNoqtl8UBsaNYXyGBQ"}
          node_locale: {eq: "en-CA"}
        ) {
          footerLinks {
            ctaLabel {
              ctaLabel
            }
            ctaHideOption
            ctaTarget
            ctaUrl
            id
          }
          logoDam {
            altText
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          footerBadges {
            ctaUrl
            ctaHideOption
            ctaIconDam {
              altText
              asset {
                url
                localImage {
                  id
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            id
          }
          accessibilityIcon {
            ctaLabel {
              ctaLabel
            }
            ctaTarget
            ctaTitle
            ctaUrl
            ctaIconDam {
              altText
              asset {
                url
                localImage {
                  id
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
          }
        }
        siteFooterFR: contentfulFooter(
          contentful_id: {eq: "zsNSNoqtl8UBsaNYXyGBQ"}
          node_locale: {eq: "fr-CA"}
        ) {
          footerLinks {
            ctaLabel {
              ctaLabel
            }
            ctaHideOption
            ctaTarget
            ctaUrl
            id
          }
          logoDam {
            altText
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          footerBadges {
            ctaUrl
            ctaHideOption
            ctaIconDam {
              altText
              asset {
                url
                localImage {
                  id
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            id
          }
          accessibilityIcon {
            ctaLabel {
              ctaLabel
            }
            ctaTarget
            ctaTitle
            ctaUrl
            ctaIconDam {
              altText
              asset {
                url
                localImage {
                  id
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
          }
        }
  }`,
  );

  const headerData = savedLanguage === 'fr' ? data.siteHeaderFR : data.siteHeaderEN;
  const footerData = savedLanguage === 'fr' ? data.siteFooterFR : data.siteFooterEN;

  return (
    <HelmetProvider>
      <ContextProviderComponent>
        <GlobalStyle
          hideBackgroundColor={showKioskSetup}
          backgroundColor={backgroundColor}
        />
        {showKioskSetup ? (
          <SetupHeader
            headerData={headerData}
            location={location}
            preferredLocale={savedLanguage}
          />
        ) : (
          <Header
            headerData={headerData}
            location={location}
            preferredLocale={savedLanguage}
            selectLocaleToSave={selectLocaleToSave}
            preferredProvince={savedProvince}
            selectProvinceToSave={selectProvinceToSave}
            skipToContentFocus={skipToContentFocus}
          />
        )}
        <Content id="main-content" tabIndex="-1" data-testid="main-content">
          {renderChildrenWithProps({
            props: {
              ...data,
              locale: locale || savedLanguage,
              location,
              preferredProvince: savedProvince,
              ipLocation,
              preferredLocale: savedLanguage,
            },
            children,
          })}
        </Content>
        {!showKioskSetup && (
          <Suspense>
            <Footer
              data={footerData}
              preferredLocale={savedLanguage}
              province={savedProvince}
              footerWrapper={footerWrapper}
            />
          </Suspense>
        )}
        <Suspense>
          <LocationBlade />
        </Suspense>
      </ContextProviderComponent>
    </HelmetProvider>
  );
};

const Content = styled.main`
  margin: 0 auto;

  &::after {
    display: table;
    clear: both;
    content: '';
  }
`;

export default Layout;
